import { AuthService } from 'src/app/services/auth.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  Input,
  ElementRef,
} from '@angular/core';
import { SecurityPolicies } from 'src/app/services/auth-variables';
import { LogService } from 'src/app/services/log.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { LocationService } from 'src/app/services/location.service';
import { delay, map, take } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DropDown } from 'src/app/interfaces/drop-down.interface';

export interface ILocationChangedToModel {
  location: DropDown<number>;
  organizationId: number;
  orgChanged: boolean;
}

@Component({
  selector: 'app-move-to-location-form',
  templateUrl: './move-to-location-form.component.html',
  styleUrls: ['./move-to-location-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveToLocationFormComponent {
  @Output() locationChanged = new EventEmitter<ILocationChangedToModel>();

  @Input() set formVM(value: {
    billableOrgId: number;
    orgId: number;
    locationId: number;
    enableLocationForm?: boolean;
  }) {
    if (value === null || value === undefined) return;
      this._initFormVM = value;

      if (!this._initFormVM.enableLocationForm) {
        this.locationFormControl.disable();
        this.orgFormControl.disable();
        this.billableOrgFormControl.disable();
        this.isEditEnabledControl.disable();
      }
    this.initializeForm();
  }

  _initFormVM: any = {
    billableOrgId: null,
    orgId: null,
    locationId: null,
  };

  formGroup: FormGroup = this.formBuilder.group({
    isEditEnabled: [false],
    billableOrg: [{ value: null, disabled: true }],
    org: [null],
    location: [null],
  });

  formControlCols: number = 9;
  securityPolicies = SecurityPolicies;
  billableOrgList: DropDown<number>[] = null;
  orgList: DropDown<number>[] = null;
  locationList: DropDown<number>[] = null;
  tempNewlyCreatedLocationId: number | null = null;

  get isEditEnabledDisabled(): Observable<boolean> {
    return this.authService.passesPolicyOf(
      this.securityPolicies.RequireSuperUser,
    );
  }

  get currentBillableOrgId() {
    return this._initFormVM?.billableOrgId;
  }

  get currentOrgId() {
    return this._initFormVM?.orgId;
  }

  get currentLocationId() {
    return this._initFormVM?.locationId;
  }

  get billableOrg() {
    return this.billableOrgFormControl?.value;
  }
  set billableOrg(value: any) {
    this.billableOrgFormControl?.setValue(value);
  }

  get org() {
    return this.orgFormControl?.value;
  }
  set org(value: any) {
    this.orgFormControl?.setValue(value);
  }

  get location() {
    return this.locationFormControl?.value;
  }
  set location(value: any) {
    this.locationFormControl?.setValue(value);
    this.emitLocationChangedEvent(value);
  }

  get billableOrgFormControl() {
    return this.formGroup.get('billableOrg');
  }
  get orgFormControl() {
    return this.formGroup.get('org');
  }
  get locationFormControl() {
    return this.formGroup.get('location');
  }

  get isEditEnabledControl() {
    return this.formGroup.get('isEditEnabled');
  }

  constructor(
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private logger: LogService,
    private organizationService: OrganizationService,
    private locationService: LocationService,
    private authService: AuthService,
    private translate: TranslateService,
    private elementRef: ElementRef,
  ) {}
  /*
   * Event Handlers
   */
  onBillableOrgValueChanged(selectedItem: DropDown<number>) {
    if (selectedItem === null || selectedItem === undefined) return;

    this.resetOrgs();
    this.resetLocations();
    this.loadOrgDropdown(selectedItem.value);
    if (selectedItem.value === this.currentBillableOrgId) {
      this.org = this.currentOrgId;
      this.location = this.currentLocationId;
      this.orgFormControl.enable();
      this.locationFormControl.enable();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.orgFormControl.enable();
    this.changeDetectorRef.markForCheck();
  }

  onOrgValueChanged(selectedItem: DropDown<number>) {
    if (selectedItem === null || selectedItem === undefined) return;

    //reset & load location dropdown.
    // if the organization is the same as the current organization, set the location to the current location.
    this.resetLocations();
    if (selectedItem.value === this.currentOrgId) {
      this.location = this.currentLocationId;
    }
    this.loadLocationDropdown(selectedItem.value);
    this.locationFormControl.enable();
    this.changeDetectorRef.markForCheck();
  }

  onLocationValueChanged(selectedItem: DropDown<number>) {
    if (selectedItem === null || selectedItem === undefined) return;

    //if add new location option is selected - Display the add new location dialog then emit new location as selected item
    if (selectedItem.value === -1) {
      this.showNewLocationForm();
      return;
    }
    this.emitLocationChangedEvent(selectedItem);
    this.changeDetectorRef.markForCheck();
  }

  onEnableEditValueChanged($event: MatCheckboxChange) {
    const isEditEnabled: boolean = $event.checked;

    this.resetOrgs();
    this.resetLocations();

    if (isEditEnabled) {
      this.billableOrgFormControl.enable();
      this.orgFormControl.disable();
      this.locationFormControl.disable();
    } else {
      setTimeout(() => {
        //reset to the initial values
        this.resetBillableOrg();
        this.org = this.currentOrgId;
        this.location = this.currentLocationId;
        this.loadOrgDropdown(this.currentBillableOrgId);
        this.loadLocationDropdown(this.currentOrgId);
        this.billableOrgFormControl.disable();
        this.orgFormControl.enable();
        this.locationFormControl.enable();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  /*
   * Private Methods
   */
  private resetBillableOrg() {
    //reset billable org dropdown value to the initial value
    this.billableOrgFormControl.setValue(this._initFormVM.billableOrgId);
  }

  private initializeForm() {
    this.billableOrg = this.currentBillableOrgId;
    this.org = this.currentOrgId;
    this.location = this.currentLocationId;

    this.loadBillableOrgDropDown();
    this.loadOrgDropdown(this.currentBillableOrgId);
    this.loadLocationDropdown(this.currentOrgId);

  }

  private resetLocations(showDefault: boolean = true): void {
    this.location = null;
    this.locationList = [];
  }

  private resetOrgs() {
    this.org = null;
    this.orgList = [];
    //this.orgFormControl.disable();
  }

  private getAddNewLocationOption(): any {
    return {
      text: '+ ' + this.translate.instant('LocationForm.AddNewLocation'),
      value: -1,
      class: 'add-new-item',
    };
  }

  private showNewLocationForm() {
    this.locationService
      .showAddNewLocationDialog(this.orgList, this.org)
      .pipe(take(1))
      .subscribe((newLocation) => {
        if (!newLocation){
          //set the location to the current location if the dialog was closed without saving
          this.location = this.currentLocationId;
          this.changeDetectorRef.markForCheck();
          return;
        }

        //org change, if not changed, at least force reload of locations for the org
        //with the new location added to it.
        this.org = newLocation.organizationId;
        const org = this.orgList.find(
          (x) => x.value === newLocation.organizationId,
        );

        //store the location id temporarily to be used to set selected item when refreshed location list is updated.
        this.tempNewlyCreatedLocationId = newLocation.locationId;

        this.onOrgValueChanged(org);

        //Process location changes
        this.emitLocationChangedEvent({
          text: newLocation.locationName,
          value: newLocation.locationId,
        });

        this.changeDetectorRef.detectChanges();
      });
  }

  private emitLocationChangedEvent(location: DropDown<number>) {
    this.locationChanged.emit({
      location: location,
      organizationId: this.org,
      orgChanged: this.org !== this.currentOrgId,
    });
  }

  /**
   * Data Load Methods
   */
  private loadBillableOrgDropDown() {
    if (this.billableOrgList?.length ?? 0 > 1) return;

    this.billableOrg = this.currentBillableOrgId;
    this.organizationService
      .getBillableOrganizationHierarchy()
      .pipe(
        map((data) => {
          return data.map((x) => {
            x.value = parseInt(x.value);
            return x;
          });
        }),
      )
      .subscribe({
        next: (data) => {
          this.billableOrgList = data;
        },
        complete: this.getDataCompleted,
      });
  }

  private loadOrgDropdown(billableOrgId: number) {
    this.orgList = []; //remove all items
    this.organizationService
      .getOrganizationDropDownByBillableOrg(billableOrgId)
      .subscribe({
        next: (data) => {
          this.orgList = data;
          this.changeDetectorRef.markForCheck();
        },
        complete: () => {
          this.getDataCompleted;
        },
      });
  }

  private loadLocationDropdown(orgId: number) {
    this.locationList = []; //remove all items
    this.locationService.getLocationDropDownListByOrgId(orgId, false).subscribe({
      next: (data) => {
        this.locationList = data ? [...data] : [];
        this.changeDetectorRef.markForCheck();
        if (
          this.tempNewlyCreatedLocationId ||
          (this.currentOrgId === orgId && this.currentLocationId)
        ) {
          const locationId =
            this.tempNewlyCreatedLocationId ?? this.currentLocationId;
          this.tempNewlyCreatedLocationId = null;

          const location = this.locationList.find(
            (x) => x.value === locationId,
          );
          if (location) {
            this.location = location.value;
            this.emitLocationChangedEvent(location);
          }
        }
      },
      complete: () => {
        //add add new location option if it doesn't exist already
        if (
          this.locationList.length === 0 ||
          (this.locationList.length > 0 && this.locationList[0].value !== -1)
        ) {
          this.locationList.unshift(this.getAddNewLocationOption());
        }
        this.getDataCompleted();
      },
    });
  }

  private getDataCompleted = () => {
    this.changeDetectorRef.markForCheck();
  };
}
